import { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
  UnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconArrowDown, IconArrowUp, IconArrowsSort, IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { Worker, WorkerRole } from "../../api/types";
import { ResponsiveTable } from "../responsiveTable/ResponsiveTable";

type WorkerSortKey = "full_name" | "role" | "phone" | "daily_rate" | "active";
type SortDir = "asc" | "desc";

interface Props {
  isAdmin: boolean;
}

export function WorkersTab({ isAdmin }: Props) {
  const { t } = useTranslation();
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [roles, setRoles] = useState<WorkerRole[]>([]);
  const [filter, setFilter] = useState<"active" | "inactive" | "all">("active");
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState<WorkerSortKey>("full_name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Worker | null>(null);
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    role_id: null as string | null,
    daily_rate: "",
    notes: "",
    active: true,
  });

  const [rolesModalOpen, setRolesModalOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");

  async function reload() {
    setLoading(true);
    try {
      const activeArg = filter === "all" ? undefined : filter === "active";
      const [w, r] = await Promise.all([
        api.listWorkers(activeArg),
        api.listWorkerRoles(),
      ]);
      setWorkers(w);
      setRoles(r);
    } catch (e) {
      notifications.show({
        color: "red",
        message: e instanceof ApiError ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, [filter]);

  function openAdd() {
    setEditTarget(null);
    setForm({ full_name: "", phone: "", role_id: null, daily_rate: "", notes: "", active: true });
    setEditOpen(true);
  }
  function openEdit(w: Worker) {
    setEditTarget(w);
    setForm({
      full_name: w.full_name,
      phone: w.phone ?? "",
      role_id: w.role_id ? String(w.role_id) : null,
      daily_rate: w.daily_rate != null ? String(w.daily_rate) : "",
      notes: w.notes ?? "",
      active: w.active,
    });
    setEditOpen(true);
  }

  async function submit() {
    if (!form.full_name.trim()) {
      notifications.show({ color: "red", message: t("workers.form.fullNameRequired") });
      return;
    }
    const body = {
      full_name: form.full_name.trim(),
      phone: form.phone.trim() || null,
      role_id: form.role_id ? Number(form.role_id) : null,
      daily_rate: form.daily_rate ? Number(form.daily_rate) : null,
      notes: form.notes.trim() || null,
    };
    try {
      if (editTarget) {
        await api.updateWorker(editTarget.id, { ...body, active: form.active });
      } else {
        await api.createWorker(body);
      }
      setEditOpen(false);
      await reload();
      notifications.show({ color: "teal", message: t("workers.form.saved") });
    } catch (e) {
      notifications.show({
        color: "red",
        message: e instanceof ApiError ? e.message : String(e),
      });
    }
  }

  async function deactivate(w: Worker) {
    if (!confirm(t("workers.confirm.deactivate", { name: w.full_name }))) return;
    try {
      await api.deleteWorker(w.id);
      await reload();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function addRole() {
    const name = newRoleName.trim();
    if (!name) return;
    try {
      await api.createWorkerRole(name);
      setNewRoleName("");
      const list = await api.listWorkerRoles();
      setRoles(list);
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }
  async function toggleRoleActive(r: WorkerRole) {
    try {
      await api.updateWorkerRole(r.id, { active: !r.active });
      setRoles(await api.listWorkerRoles());
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  const roleOptions = roles
    .filter((r) => r.active || (editTarget && r.id === editTarget.role_id))
    .map((r) => ({ value: String(r.id), label: r.name }));

  const roleById = new Map(roles.map((r) => [r.id, r.name] as const));

  function toggleSort(key: WorkerSortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "daily_rate" || key === "active" ? "desc" : "asc");
    }
  }

  const displayedRows = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const sortable = [...workers];
    sortable.sort((a, b) => {
      let av: string | number = "";
      let bv: string | number = "";
      switch (sortKey) {
        case "full_name": av = a.full_name; bv = b.full_name; break;
        case "role":
          av = a.role_id ? (roleById.get(a.role_id) ?? "") : "";
          bv = b.role_id ? (roleById.get(b.role_id) ?? "") : "";
          break;
        case "phone": av = a.phone ?? ""; bv = b.phone ?? ""; break;
        case "daily_rate": av = a.daily_rate ?? -Infinity; bv = b.daily_rate ?? -Infinity; break;
        case "active": av = a.active ? 1 : 0; bv = b.active ? 1 : 0; break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return sortable;
  }, [workers, sortKey, sortDir, roleById]);

  function SortHeader({ k, label }: { k: WorkerSortKey; label: string }) {
    const isActive = sortKey === k;
    const Icon = isActive ? (sortDir === "asc" ? IconArrowUp : IconArrowDown) : IconArrowsSort;
    return (
      <UnstyledButton
        onClick={() => toggleSort(k)}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", fontWeight: 600 }}
      >
        {label}
        <Icon size={12} style={{ opacity: isActive ? 1 : 0.4 }} />
      </UnstyledButton>
    );
  }

  return (
    <Stack>
      <Group justify="space-between" wrap="wrap">
        <Group gap="xs" wrap="wrap">
          <Select
            data={[
              { value: "active", label: t("workers.filter.active") },
              { value: "inactive", label: t("workers.filter.inactive") },
              { value: "all", label: t("workers.filter.all") },
            ]}
            value={filter}
            onChange={(v) => setFilter((v as "active" | "inactive" | "all") ?? "active")}
            allowDeselect={false}
            w={160}
          />
        </Group>
        {isAdmin && (
          <Group gap="xs">
            <Button leftSection={<IconPlus size={14} />} onClick={openAdd}>
              {t("workers.addWorker")}
            </Button>
            <Button variant="default" onClick={() => setRolesModalOpen(true)}>
              {t("workers.manageRoles")}
            </Button>
          </Group>
        )}
      </Group>

      <ResponsiveTable
        mobile={{
          rows: displayedRows,
          sort: {
            options: [
              { value: "full_name", label: t("workers.col.name") },
              { value: "role", label: t("workers.col.role") },
              { value: "phone", label: t("workers.col.phone") },
              ...(isAdmin ? [{ value: "daily_rate", label: t("workers.col.dailyRate") }] : []),
              { value: "active", label: t("workers.col.status") },
            ],
            value: sortKey,
            dir: sortDir,
            onChange: (v, d) => (v === sortKey ? setSortDir(d) : toggleSort(v as WorkerSortKey)),
          },
          rowKey: (w) => w.id,
          title: (w) => w.full_name,
          subtitle: (w) => (w.role_id ? roleById.get(w.role_id) ?? "—" : "—"),
          badge: (w) => (
            <Badge color={w.active ? "teal" : "gray"} variant="light">
              {w.active ? t("workers.status.active") : t("workers.status.inactive")}
            </Badge>
          ),
          fields: [
            { label: t("workers.col.phone"), render: (w) => w.phone ?? "—" },
            ...(isAdmin
              ? [{ label: t("workers.col.dailyRate"), render: (w: Worker) => (w.daily_rate != null ? w.daily_rate.toFixed(2) : "—") }]
              : []),
          ],
          actions: isAdmin
            ? (w) => (
                <Group gap="xs" wrap="nowrap" justify="flex-end">
                  <ActionIcon size="lg" variant="subtle" onClick={() => openEdit(w)} title={t("common.edit")}>
                    <IconEdit size={16} />
                  </ActionIcon>
                  {w.active && (
                    <ActionIcon size="lg" color="red" variant="subtle" onClick={() => void deactivate(w)} title={t("workers.deactivate")}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  )}
                </Group>
              )
            : undefined,
          loading: loading && workers.length === 0,
          empty: t("workers.empty"),
        }}
      >
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th><SortHeader k="full_name" label={t("workers.col.name")} /></Table.Th>
            <Table.Th><SortHeader k="role" label={t("workers.col.role")} /></Table.Th>
            <Table.Th><SortHeader k="phone" label={t("workers.col.phone")} /></Table.Th>
            {isAdmin && <Table.Th><SortHeader k="daily_rate" label={t("workers.col.dailyRate")} /></Table.Th>}
            <Table.Th><SortHeader k="active" label={t("workers.col.status")} /></Table.Th>
            {isAdmin && <Table.Th />}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {workers.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={isAdmin ? 6 : 4}>
                <Text c="dimmed" size="sm">{loading ? t("common.loading") : t("workers.empty")}</Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            displayedRows.map((w) => (
              <Table.Tr key={w.id}>
                <Table.Td>{w.full_name}</Table.Td>
                <Table.Td>{w.role_id ? roleById.get(w.role_id) ?? "—" : "—"}</Table.Td>
                <Table.Td>{w.phone ?? "—"}</Table.Td>
                {isAdmin && <Table.Td>{w.daily_rate != null ? w.daily_rate.toFixed(2) : "—"}</Table.Td>}
                <Table.Td>
                  <Badge color={w.active ? "teal" : "gray"} variant="light">
                    {w.active ? t("workers.status.active") : t("workers.status.inactive")}
                  </Badge>
                </Table.Td>
                {isAdmin && (
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap" justify="flex-end">
                      <ActionIcon variant="subtle" onClick={() => openEdit(w)} title={t("common.edit")}>
                        <IconEdit size={14} />
                      </ActionIcon>
                      {w.active && (
                        <ActionIcon color="red" variant="subtle" onClick={() => void deactivate(w)} title={t("workers.deactivate")}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      )}
                    </Group>
                  </Table.Td>
                )}
              </Table.Tr>
            ))
          )}
        </Table.Tbody>
      </Table>
      </ResponsiveTable>

      <Modal opened={editOpen} onClose={() => setEditOpen(false)} title={editTarget ? t("workers.modal.editTitle") : t("workers.modal.addTitle")} centered>
        <Stack>
          <TextInput
            label={t("workers.col.name")}
            required
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.currentTarget.value })}
          />
          <TextInput
            label={t("workers.col.phone")}
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.currentTarget.value })}
          />
          <Select
            label={t("workers.col.role")}
            data={roleOptions}
            value={form.role_id}
            onChange={(v) => setForm({ ...form, role_id: v })}
            clearable
            searchable
          />
          <NumberInput
            label={t("workers.col.dailyRate")}
            value={form.daily_rate}
            onChange={(v) => setForm({ ...form, daily_rate: typeof v === "number" ? String(v) : (v ?? "") })}
            min={0}
            decimalScale={2}
            allowNegative={false}
          />
          <Textarea
            label={t("workers.col.notes")}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.currentTarget.value })}
            minRows={2}
          />
          {editTarget && (
            <Switch
              label={t("workers.col.status")}
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.currentTarget.checked })}
            />
          )}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setEditOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={() => void submit()}>{t("common.save")}</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={rolesModalOpen} onClose={() => setRolesModalOpen(false)} title={t("workers.manageRoles")} centered>
        <Stack>
          <Group>
            <TextInput
              placeholder={t("workers.role.newPlaceholder")}
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Button onClick={() => void addRole()}>{t("workers.role.add")}</Button>
          </Group>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("workers.role.name")}</Table.Th>
                <Table.Th>{t("workers.role.active")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {roles.map((r) => (
                <Table.Tr key={r.id}>
                  <Table.Td>{r.name}</Table.Td>
                  <Table.Td>
                    <Switch checked={r.active} onChange={() => void toggleRoleActive(r)} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      </Modal>
    </Stack>
  );
}
